import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);          // null = unknown/checking, false = anon, object = signed in
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // No token AND no cookie → skip the /me probe entirely so we don't
        // generate a 401 on a fresh visit.
        if (!getToken() && !document.cookie.includes("access_token=")) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
            setToken("");   // clear any stale token
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // Persist the bearer token so every subsequent request authenticates
            // regardless of whether cookies survive cross-site browser policies.
            if (data?.access_token) setToken(data.access_token);
            // Strip the tokens out of the user object before storing in React state.
            // eslint-disable-next-line no-unused-vars
            const { access_token, refresh_token, token_type, ...userOnly } = data || {};
            setUser(userOnly);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setToken("");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
